import classNames from 'classnames';
import { FC, ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { NumberFormatValues, NumericFormat, SourceInfo } from 'react-number-format';
import CharacterCount from '../Input/CharacterCount/CharacterCount';
import styles from '../Input/Input.module.css';
import { InputStates } from '../Input/Input.types';
import Tooltip from '../Tooltip/Tooltip';
import { PositionTypes } from '../Tooltip/Tooltip.types';
import { MaskedInputProps } from './MaskedInput.types';

const MaskedInput: FC<MaskedInputProps> = forwardRef(
  (
    {
      id,
      label,
      helpText,
      subText,
      tooltipText,
      optionalLabelText,
      value,
      disabled,
      state = InputStates.Default,
      characterLimit,
      className,
      onValueChange,
      ...props
    },
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const [inputValue, setInputValue] = useState(value || '');

    useEffect(() => {
      setInputValue(value || '');
    }, [value]);

    const handleValueChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
      if (onValueChange) onValueChange(values, sourceInfo);
      setInputValue(values.formattedValue);
    };

    return (
      <div className={styles.container}>
        <div className={styles.inputLabelWrapper}>
          <label htmlFor={id} className={styles.label}>
            {label}
          </label>
          {optionalLabelText && <label htmlFor={id} className={styles.optionalText}>{` ${optionalLabelText}`}</label>}
          {tooltipText && <Tooltip content={tooltipText} size={18} position={PositionTypes.Top} />}
          {helpText !== undefined && <div className={styles.helptext}>{helpText}</div>}
        </div>
        <div className={styles.inputContainer}>
          <NumericFormat
            {...props}
            id={id}
            getInputRef={ref}
            onValueChange={handleValueChange}
            value={inputValue}
            disabled={disabled}
            data-testid="MaskedInput"
            className={classNames({
              [styles.input]: true,
              [styles.errorinput]: state === InputStates.Error,
              [className!]: className !== undefined,
            })}
          />
        </div>
        {characterLimit !== undefined && (
          <CharacterCount charactersRemaining={characterLimit - (inputValue?.length || 0)} />
        )}
        {subText !== undefined && (
          <div
            aria-live={state === InputStates.Error ? 'assertive' : 'off'}
            className={classNames({
              [styles.subtext]: true,
              [styles.errortext]: state === InputStates.Error,
            })}
          >
            {subText}
          </div>
        )}
      </div>
    );
  },
);

MaskedInput.displayName = 'MaskedInput';

export default MaskedInput;
